import { ChangeDetectionStrategy, Component, forwardRef, Input, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { PageFilterGroupType } from '@common/paged-data/enums/page-filter-group-type.enum';
import { PageFilterType } from '@common/paged-data/enums/page-filter-type.enum';
import { Page } from '@common/paged-data/types/page.type';
import { createEmptyPage } from '@common/paged-data/util/page';
import { AnyBranchId } from '@portal-core/branches/constants/any-branch-id.constant';
import { Branch } from '@portal-core/branches/models/branch.model';
import { BranchesService } from '@portal-core/branches/services/branches.service';
import { BranchesFilterConfig } from '@portal-core/branches/util/branches-filter-config';
import { ProcessAttemptStatus } from '@portal-core/processes/enums/process-attempt-status.enum';
import { SelectListBase } from '@portal-core/ui/list/util/select-list-base';
import { SelectionListControl } from '@portal-core/ui/list/util/selection-list-control';
import { PageFilterService } from '@portal-core/ui/page-filters/services/page-filter.service';
import { Observable, of, tap } from 'rxjs';

@Component({
  selector: 'mc-branches-select-list',
  templateUrl: './branches-select-list.component.html',
  styleUrls: ['./branches-select-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Provide SelectListBase as BranchesSelectList so that this component can work in mc-autocomplete
  providers: [{ provide: SelectListBase, useExisting: forwardRef(() => BranchesSelectListComponent) }]
})
export class BranchesSelectListComponent extends SelectListBase<Branch> implements OnChanges {
  /** The process states to filter by. Defaults to builds in any state. */
  @Input() statuses?: ProcessAttemptStatus[];
  /** The project id that the branches belong to. */
  @Input() projectId: number;
  /** Whether to include the Any branch option in the list. */
  @Input() showAnyBranchOption?: boolean = false;
  /** The target path that must exist in the branches. */
  @Input() targetPath?: string;
  /** The source of the branch list. Defaults to branches from the project. */
  @Input() type?: 'project' | 'builds' = 'project';

  filterConfig: BranchesFilterConfig = new BranchesFilterConfig(['BranchName', 'Name']);

  listControl: SelectionListControl<Branch> = new SelectionListControl<Branch>(this.branchesService, filter => {
    let fetch$: Observable<Page<Branch>> = of(createEmptyPage<Branch>());

    if (typeof this.projectId === 'number' && this.projectId > 0) {
      if (this.type === 'builds') {
        fetch$ = this.branchesService.getBuildBranchesPageByProjectId$(this.projectId, filter);
      } else {
        fetch$ = this.branchesService.getProjectBranchesPageByProjectId$(this.projectId, filter);
      }
    }

    return fetch$.pipe(
      tap(page => {
        if (this.showAnyBranchOption) {
          if (page.Page === 0) {
            page.Items.splice(0, 0, this.branchesService.getItemById(AnyBranchId));
          }
        }
      })
    );
  }, branch => {
    return branch.Id;
  });

  constructor(private branchesService: BranchesService, private pageFilterService: PageFilterService) {
    super(branchesService);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.statuses) {
      this.applyFilter$('branches-select-list-statuses', this.pageFilterService.create({
        Id: 'branches-select-list-statuses',
        Type: PageFilterGroupType.Select
      }).select('Process.LastProcessAttempt.Status', this.statuses).value);
    }

    if (changes.targetPath) {
      this.applyFilter$('branches-select-list-target-path', this.pageFilterService.create({
        Id: 'branches-select-list-target-path',
        Type: PageFilterGroupType.String
      }).string('TargetPath', this.targetPath, { FilterType: PageFilterType.Equals }).value);
    }

    if (changes.projectId || changes.showAnyBranchOption || changes.type) {
      this.hardReload();
    }
  }
}
